// NEKASSUR — Catalogue des solutions d'assurance (bilingue).
// Ajouter une nouvelle assurance = ajouter un objet ici. Le design est réutilisé.
// category: "particuliers" | "professionnels" | "epargne"

const basePaths = {
  particuliers: "/particuliers",
  professionnels: "/professionnels",
  epargne: "",
};

export const services = [
  // ---------------------- PARTICULIERS ----------------------
  {
    slug: "assurance-auto",
    category: "particuliers",
    icon: "Car",
    image: "https://images.unsplash.com/photo-1611448746128-7c39e03b71e4",
    fr: {
      name: "Assurance auto",
      benefit: "Rouler serein, même avec un malus ou après une résiliation.",
      heroTitle: "Une assurance auto adaptée, y compris pour les profils difficiles.",
      heroText: "Que votre situation soit classique ou plus complexe (malus, résiliation, sinistres), nous recherchons une couverture au juste niveau, sans refus automatique.",
      profiles: ["Conducteurs particuliers", "Jeunes conducteurs", "Conducteurs malussés ou résiliés", "Véhicules spécifiques ou de valeur"],
      risks: ["Responsabilité civile obligatoire", "Dommages tous accidents ou au tiers", "Vol, incendie, bris de glace", "Assistance et véhicule de remplacement", "Garantie du conducteur"],
      complex: ["Malus élevé", "Résiliation pour sinistres ou non-paiement", "Antécédents de sinistralité importants", "Relevé d'information défavorable"],
      prepare: ["Votre relevé d'information (RI)", "La carte grise du véhicule", "Un historique rapide de votre situation"],
      faq: [
        { q: "Assurez-vous les conducteurs résiliés ?", a: "Nous étudions ces dossiers au cas par cas et interrogeons les partenaires adaptés. Nous ne garantissons pas l'acceptation, mais nous cherchons activement une solution." },
        { q: "Puis-je être assuré avec un malus important ?", a: "Souvent oui, à des conditions spécifiques. L'important est de présenter correctement votre dossier." },
      ],
    },
    en: {
      name: "Car insurance",
      benefit: "Drive with peace of mind, even with a penalty or after a cancellation.",
      heroTitle: "Car insurance that fits, including for difficult profiles.",
      heroText: "Whether your situation is standard or more complex (penalty, cancellation, claims), we look for cover at the right level, without automatic refusal.",
      profiles: ["Individual drivers", "Young drivers", "Penalised or cancelled drivers", "Specific or high-value vehicles"],
      risks: ["Mandatory third-party liability", "Comprehensive or third-party damage", "Theft, fire, glass breakage", "Assistance and replacement vehicle", "Driver's cover"],
      complex: ["High penalty (malus)", "Cancellation for claims or non-payment", "Significant claims history", "Unfavourable insurance record"],
      prepare: ["Your insurance record (relevé d'information)", "The vehicle registration document", "A quick summary of your situation"],
      faq: [
        { q: "Do you insure cancelled drivers?", a: "We study these cases individually and contact suitable partners. We don't guarantee acceptance, but we actively search for a solution." },
        { q: "Can I be insured with a high penalty?", a: "Often yes, under specific conditions. What matters is presenting your case properly." },
      ],
    },
  },
  {
    slug: "assurance-habitation",
    category: "particuliers",
    icon: "Home",
    image: "https://images.pexels.com/photos/8054853/pexels-photo-8054853.jpeg",
    fr: {
      name: "Assurance habitation",
      benefit: "Protéger votre logement, vos biens et votre responsabilité.",
      heroTitle: "Votre logement protégé, propriétaire comme locataire.",
      heroText: "Nous recherchons une multirisque habitation cohérente avec votre situation réelle : surface, valeur des biens, usage et éventuels antécédents.",
      profiles: ["Locataires", "Propriétaires occupants", "Propriétaires bailleurs", "Résidences secondaires"],
      risks: ["Incendie, dégâts des eaux, catastrophes naturelles", "Vol et vandalisme", "Responsabilité civile vie privée", "Bris de glace", "Dommages aux biens de valeur"],
      complex: ["Sinistralité habitation répétée", "Résiliation par un précédent assureur", "Logement atypique ou en zone à risque"],
      prepare: ["La surface et le type de logement", "Un descriptif des biens de valeur", "Vos éventuels antécédents de sinistres"],
      faq: [
        { q: "Puis-je m'assurer après une résiliation habitation ?", a: "Oui, nous étudions ces dossiers et présentons votre situation aux partenaires adaptés." },
      ],
    },
    en: {
      name: "Home insurance",
      benefit: "Protect your home, belongings and liability.",
      heroTitle: "Your home protected, as owner or tenant.",
      heroText: "We look for home multi-risk cover consistent with your real situation: size, value of belongings, use and any prior history.",
      profiles: ["Tenants", "Owner-occupiers", "Landlords", "Second homes"],
      risks: ["Fire, water damage, natural disasters", "Theft and vandalism", "Private-life liability", "Glass breakage", "Damage to valuables"],
      complex: ["Repeated home claims", "Cancellation by a previous insurer", "Atypical home or high-risk area"],
      prepare: ["The size and type of home", "A description of valuables", "Any prior claims history"],
      faq: [{ q: "Can I get cover after a home policy cancellation?", a: "Yes, we study these cases and present your situation to suitable partners." }],
    },
  },
  {
    slug: "mutuelle-sante",
    category: "particuliers",
    icon: "HeartPulse",
    image: "https://images.pexels.com/photos/8460047/pexels-photo-8460047.jpeg",
    fr: {
      name: "Mutuelle santé",
      benefit: "Des remboursements adaptés à vos besoins réels de santé.",
      heroTitle: "Une complémentaire santé au juste niveau, sans payer pour l'inutile.",
      heroText: "Nous cherchons un contrat cohérent avec vos besoins : optique, dentaire, hospitalisation, médecines douces, selon votre situation et votre budget.",
      profiles: ["Particuliers et familles", "Indépendants (Madelin)", "Séniors", "Jeunes actifs"],
      risks: ["Hospitalisation", "Optique et dentaire", "Consultations et spécialistes", "Médecines douces", "Dépassements d'honoraires"],
      complex: ["Besoins de santé importants", "Recherche d'un meilleur rapport garanties / cotisation", "Passage d'un contrat collectif à un contrat individuel"],
      prepare: ["Votre contrat actuel s'il existe", "Vos priorités de remboursement", "Votre budget indicatif"],
      faq: [{ q: "Pouvez-vous comparer plusieurs mutuelles ?", a: "Nous interrogeons plusieurs partenaires pour trouver un contrat adapté, sans prétendre comparer tout le marché." }],
    },
    en: {
      name: "Health cover",
      benefit: "Reimbursements matched to your real health needs.",
      heroTitle: "Health cover at the right level, without paying for the unnecessary.",
      heroText: "We look for a contract consistent with your needs: optical, dental, hospitalisation, alternative medicine, depending on your situation and budget.",
      profiles: ["Individuals and families", "Self-employed", "Seniors", "Young working people"],
      risks: ["Hospitalisation", "Optical and dental", "Consultations and specialists", "Alternative medicine", "Fee overruns"],
      complex: ["Significant health needs", "Better cover-to-premium ratio", "Moving from group to individual cover"],
      prepare: ["Your current contract if any", "Your reimbursement priorities", "Your indicative budget"],
      faq: [{ q: "Can you compare several health plans?", a: "We reach out to several partners to find a suitable contract, without claiming to compare the whole market." }],
    },
  },
  {
    slug: "assurance-emprunteur",
    category: "particuliers",
    icon: "Landmark",
    image: "https://images.pexels.com/photos/7821486/pexels-photo-7821486.jpeg",
    fr: {
      name: "Assurance emprunteur",
      benefit: "Sécuriser votre crédit, souvent à de meilleures conditions.",
      heroTitle: "Assurez votre prêt et faites valoir votre droit au changement.",
      heroText: "Nous recherchons une couverture adaptée à votre profil et à votre crédit, y compris en cas de risque de santé ou de refus antérieur.",
      profiles: ["Emprunteurs immobiliers", "Personnes souhaitant changer d'assurance de prêt", "Profils avec risque aggravé de santé"],
      risks: ["Décès", "Perte totale et irréversible d'autonomie", "Incapacité et invalidité", "Perte d'emploi (selon contrats)"],
      complex: ["Risque aggravé de santé", "Profession ou pratique sportive à risque", "Refus ou surprime chez l'assureur bancaire"],
      prepare: ["Votre offre de prêt ou tableau d'amortissement", "Les garanties exigées par la banque", "Un point rapide sur votre situation"],
      faq: [
        { q: "Puis-je changer l'assurance de mon prêt en cours ?", a: "Oui, la réglementation permet de substituer votre assurance emprunteur. Nous vous accompagnons dans la démarche." },
        { q: "Et en cas de problème de santé ?", a: "Nous étudions les profils avec risque aggravé et présentons votre dossier aux partenaires adaptés." },
      ],
    },
    en: {
      name: "Loan insurance",
      benefit: "Secure your loan, often on better terms.",
      heroTitle: "Insure your loan and exercise your right to switch.",
      heroText: "We look for cover suited to your profile and loan, including in case of a health risk or a prior refusal.",
      profiles: ["Property borrowers", "People wishing to switch loan insurance", "Profiles with aggravated health risk"],
      risks: ["Death", "Total and irreversible loss of autonomy", "Incapacity and disability", "Job loss (depending on contract)"],
      complex: ["Aggravated health risk", "High-risk profession or sport", "Refusal or surcharge from the bank insurer"],
      prepare: ["Your loan offer or amortisation table", "The cover required by the bank", "A quick review of your situation"],
      faq: [
        { q: "Can I switch my current loan insurance?", a: "Yes, regulations allow you to substitute your loan insurance. We support you through the process." },
        { q: "What about a health issue?", a: "We study profiles with aggravated risk and present your case to suitable partners." },
      ],
    },
  },
  {
    slug: "prevoyance",
    category: "particuliers",
    icon: "ShieldCheck",
    image: "https://images.pexels.com/photos/7551659/pexels-photo-7551659.jpeg",
    fr: {
      name: "Prévoyance",
      benefit: "Protéger vos revenus et vos proches face aux aléas de la vie.",
      heroTitle: "Anticiper l'imprévu pour protéger ceux qui comptent.",
      heroText: "Arrêt de travail, invalidité, décès : nous recherchons une prévoyance cohérente avec votre statut, vos revenus et vos priorités familiales.",
      profiles: ["Indépendants et TNS", "Salariés souhaitant compléter leur protection", "Chefs de famille", "Dirigeants"],
      risks: ["Maintien de revenus en cas d'arrêt de travail", "Invalidité", "Capital ou rente décès", "Protection des proches"],
      complex: ["Statut de travailleur non salarié", "Revenus variables", "Antécédents de santé"],
      prepare: ["Votre statut professionnel", "Vos revenus indicatifs", "Vos priorités de protection"],
      faq: [{ q: "La prévoyance est-elle utile si je suis déjà salarié ?", a: "Souvent oui, pour compléter des garanties parfois limitées et protéger réellement vos revenus et vos proches." }],
    },
    en: {
      name: "Personal protection",
      benefit: "Protect your income and loved ones against life's uncertainties.",
      heroTitle: "Anticipate the unexpected to protect those who matter.",
      heroText: "Sick leave, disability, death: we look for protection consistent with your status, income and family priorities.",
      profiles: ["Self-employed", "Employees wanting to top up cover", "Heads of family", "Company directors"],
      risks: ["Income continuity during sick leave", "Disability", "Death capital or annuity", "Protection of loved ones"],
      complex: ["Self-employed status", "Variable income", "Health history"],
      prepare: ["Your professional status", "Your indicative income", "Your protection priorities"],
      faq: [{ q: "Is protection useful if I'm already an employee?", a: "Often yes, to top up sometimes limited cover and genuinely protect your income and loved ones." }],
    },
  },
  // ---------------------- PROFESSIONNELS ----------------------
  {
    slug: "rc-professionnelle",
    category: "professionnels",
    icon: "Scale",
    image: "https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg",
    fr: {
      name: "Responsabilité civile professionnelle",
      benefit: "Couvrir les dommages causés dans le cadre de votre activité.",
      heroTitle: "Exercer sereinement, votre responsabilité couverte.",
      heroText: "Nous recherchons une RC Pro adaptée à votre métier et à vos obligations, y compris pour les activités spécifiques ou récentes.",
      profiles: ["Indépendants et professions libérales", "Consultants et prestataires", "Métiers réglementés", "TPE / PME"],
      risks: ["Dommages corporels, matériels et immatériels causés à des tiers", "Fautes, erreurs ou négligences professionnelles", "Défense et recours"],
      complex: ["Activité récente ou en création", "Métier spécifique ou peu standard", "Antécédents de sinistres professionnels"],
      prepare: ["La description précise de votre activité", "Votre chiffre d'affaires indicatif", "Vos obligations réglementaires éventuelles"],
      faq: [{ q: "La RC Pro est-elle obligatoire ?", a: "Elle l'est pour de nombreuses professions réglementées et fortement recommandée pour les autres. Nous vérifions vos obligations avec vous." }],
    },
    en: {
      name: "Professional liability",
      benefit: "Cover damage caused in the course of your activity.",
      heroTitle: "Work with peace of mind, your liability covered.",
      heroText: "We look for professional liability cover suited to your trade and obligations, including for specific or recent activities.",
      profiles: ["Self-employed & liberal professions", "Consultants and service providers", "Regulated professions", "Small & medium businesses"],
      risks: ["Bodily, material and immaterial damage to third parties", "Professional faults, errors or negligence", "Defence and recourse"],
      complex: ["Recent or newly created activity", "Specific or non-standard trade", "Prior professional claims"],
      prepare: ["A precise description of your activity", "Your indicative turnover", "Any regulatory obligations"],
      faq: [{ q: "Is professional liability mandatory?", a: "It is for many regulated professions and strongly recommended for others. We check your obligations with you." }],
    },
  },
  {
    slug: "multirisque-professionnelle",
    category: "professionnels",
    icon: "Building2",
    image: "https://images.pexels.com/photos/5531634/pexels-photo-5531634.jpeg",
    fr: {
      name: "Multirisque professionnelle",
      benefit: "Protéger vos locaux, votre matériel et votre exploitation.",
      heroTitle: "Vos locaux et votre activité, protégés au bon niveau.",
      heroText: "Nous recherchons une multirisque cohérente avec votre métier : locaux, stocks, matériel, perte d'exploitation et responsabilités.",
      profiles: ["Commerçants", "Artisans", "Bureaux et cabinets", "TPE / PME avec locaux"],
      risks: ["Incendie, dégâts des eaux, événements climatiques", "Vol et vandalisme", "Matériel et marchandises", "Perte d'exploitation", "Responsabilité d'exploitation"],
      complex: ["Locaux atypiques ou en zone à risque", "Stocks ou matériels de forte valeur", "Sinistralité passée"],
      prepare: ["La description de vos locaux et de leur usage", "La valeur de votre matériel et de vos stocks", "Vos éventuels antécédents"],
      faq: [{ q: "Puis-je adapter les garanties à mon métier ?", a: "Oui, c'est tout l'intérêt d'une multirisque professionnelle bien construite. Nous l'ajustons à votre activité réelle." }],
    },
    en: {
      name: "Professional multi-risk",
      benefit: "Protect your premises, equipment and operations.",
      heroTitle: "Your premises and activity, protected at the right level.",
      heroText: "We look for multi-risk cover consistent with your trade: premises, stock, equipment, business interruption and liabilities.",
      profiles: ["Retailers", "Craftspeople", "Offices and practices", "Small & medium businesses with premises"],
      risks: ["Fire, water damage, weather events", "Theft and vandalism", "Equipment and goods", "Business interruption", "Operating liability"],
      complex: ["Atypical or high-risk premises", "High-value stock or equipment", "Past claims history"],
      prepare: ["A description of your premises and use", "The value of your equipment and stock", "Any prior history"],
      faq: [{ q: "Can I tailor the cover to my trade?", a: "Yes, that is the point of a well-built professional multi-risk. We adjust it to your real activity." }],
    },
  },
  {
    slug: "assurance-decennale",
    category: "professionnels",
    icon: "HardHat",
    image: "https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg",
    fr: {
      name: "Responsabilité civile décennale",
      benefit: "Répondre à votre obligation légale dans le BTP.",
      heroTitle: "Bâtir en règle, votre décennale sécurisée.",
      heroText: "Obligatoire pour les métiers du bâtiment, la décennale demande une présentation soignée de votre activité. Nous recherchons une solution adaptée, y compris en création.",
      profiles: ["Artisans du bâtiment", "Entreprises de construction", "Auto-entrepreneurs du BTP", "Sous-traitants"],
      risks: ["Dommages compromettant la solidité de l'ouvrage", "Dommages rendant l'ouvrage impropre à sa destination", "Garantie sur dix ans après réception"],
      complex: ["Entreprise nouvellement créée", "Corps de métier multiples", "Antécédents ou reprise d'activité"],
      prepare: ["La liste précise de vos activités BTP", "Vos qualifications et expérience", "Votre chiffre d'affaires prévisionnel"],
      faq: [{ q: "Puis-je obtenir une décennale en création d'entreprise ?", a: "Oui, c'est fréquent. La clé est de présenter clairement vos activités et votre expérience aux partenaires adaptés." }],
    },
    en: {
      name: "Decennial liability",
      benefit: "Meet your legal obligation in construction.",
      heroTitle: "Build compliantly, your decennial cover secured.",
      heroText: "Mandatory for construction trades, decennial cover requires a careful presentation of your activity. We look for a suitable solution, including for start-ups.",
      profiles: ["Building craftspeople", "Construction companies", "Self-employed builders", "Subcontractors"],
      risks: ["Damage affecting the structural soundness", "Damage making the work unfit for purpose", "Ten-year cover after handover"],
      complex: ["Newly created company", "Multiple trades", "Prior history or resumed activity"],
      prepare: ["A precise list of your construction activities", "Your qualifications and experience", "Your projected turnover"],
      faq: [{ q: "Can I get decennial cover for a start-up?", a: "Yes, it's common. The key is to clearly present your activities and experience to suitable partners." }],
    },
  },
  {
    slug: "flotte-automobile",
    category: "professionnels",
    icon: "Truck",
    image: "https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg",
    fr: {
      name: "Flotte automobile",
      benefit: "Gérer et couvrir tous vos véhicules professionnels.",
      heroTitle: "Vos véhicules professionnels, une gestion simplifiée.",
      heroText: "Nous recherchons une solution de flotte adaptée à votre parc, avec une gestion facilitée et des garanties cohérentes avec votre usage.",
      profiles: ["Entreprises avec plusieurs véhicules", "Artisans et BTP", "Sociétés de transport léger", "TPE / PME en développement"],
      risks: ["Responsabilité civile des véhicules", "Dommages, vol, incendie", "Bris de glace et assistance", "Marchandises transportées (selon contrats)"],
      complex: ["Sinistralité de flotte élevée", "Véhicules variés ou spécifiques", "Croissance rapide du parc"],
      prepare: ["La liste de vos véhicules", "Votre historique de sinistralité", "L'usage réel des véhicules"],
      faq: [{ q: "À partir de combien de véhicules une flotte est-elle pertinente ?", a: "Souvent dès quelques véhicules, pour simplifier la gestion et harmoniser les garanties. Nous l'étudions avec vous." }],
    },
    en: {
      name: "Vehicle fleet",
      benefit: "Manage and cover all your professional vehicles.",
      heroTitle: "Your professional vehicles, simplified management.",
      heroText: "We look for a fleet solution suited to your vehicles, with easier management and cover consistent with your use.",
      profiles: ["Businesses with several vehicles", "Craftspeople & construction", "Light transport companies", "Growing small & medium businesses"],
      risks: ["Vehicle third-party liability", "Damage, theft, fire", "Glass breakage and assistance", "Transported goods (depending on contract)"],
      complex: ["High fleet claims history", "Varied or specific vehicles", "Rapidly growing fleet"],
      prepare: ["The list of your vehicles", "Your claims history", "The real use of the vehicles"],
      faq: [{ q: "From how many vehicles is a fleet relevant?", a: "Often from just a few vehicles, to simplify management and harmonise cover. We study it with you." }],
    },
  },
  {
    slug: "assurances-collectives",
    category: "professionnels",
    icon: "Users",
    image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952",
    fr: {
      name: "Santé & prévoyance collectives",
      benefit: "Protéger vos salariés et remplir vos obligations d'employeur.",
      heroTitle: "Des garanties collectives attractives pour vos équipes.",
      heroText: "Mutuelle et prévoyance d'entreprise : nous recherchons des contrats conformes à vos obligations et attractifs pour fidéliser vos collaborateurs.",
      profiles: ["Entreprises avec salariés", "TPE / PME", "Employeurs soumis à un accord de branche", "Dirigeants souhaitant fidéliser"],
      risks: ["Complémentaire santé collective obligatoire", "Prévoyance des salariés", "Garanties cadres et non-cadres", "Portabilité des droits"],
      complex: ["Obligations de branche spécifiques", "Effectifs variés", "Optimisation garanties / budget"],
      prepare: ["Votre effectif et vos catégories de personnel", "Votre convention collective", "Vos contrats actuels s'ils existent"],
      faq: [{ q: "La mutuelle d'entreprise est-elle obligatoire ?", a: "Oui, pour la complémentaire santé des salariés du privé, avec des règles précises. Nous vérifions votre conformité." }],
    },
    en: {
      name: "Group health & protection",
      benefit: "Protect your employees and meet your employer obligations.",
      heroTitle: "Attractive group cover for your teams.",
      heroText: "Company health and protection: we look for contracts compliant with your obligations and attractive to retain your staff.",
      profiles: ["Businesses with employees", "Small & medium businesses", "Employers under a branch agreement", "Directors wishing to build loyalty"],
      risks: ["Mandatory group health cover", "Employee protection", "Executive and non-executive cover", "Portability of rights"],
      complex: ["Specific branch obligations", "Varied workforce", "Cover-to-budget optimisation"],
      prepare: ["Your headcount and staff categories", "Your collective agreement", "Your current contracts if any"],
      faq: [{ q: "Is company health cover mandatory?", a: "Yes, for private-sector employees' health cover, with precise rules. We check your compliance." }],
    },
  },
  {
    slug: "garantie-financiere",
    category: "professionnels",
    icon: "BadgeCheck",
    image: "https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg",
    fr: {
      name: "Garantie financière",
      benefit: "Répondre aux exigences réglementaires de votre profession.",
      heroTitle: "La garantie financière exigée par votre activité.",
      heroText: "Certaines professions doivent justifier d'une garantie financière. Nous recherchons une solution adaptée à votre statut et à vos obligations.",
      profiles: ["Agents immobiliers", "Agences de voyage", "Mandataires et professions réglementées", "Entreprises soumises à obligation"],
      risks: ["Représentation des fonds détenus pour des tiers", "Conformité réglementaire", "Sécurisation des clients et partenaires"],
      complex: ["Activité nouvellement créée", "Montants de garantie élevés", "Situation financière à présenter"],
      prepare: ["La nature exacte de votre obligation", "Le montant de garantie requis", "Vos éléments d'activité"],
      faq: [{ q: "Quelles professions sont concernées ?", a: "Notamment l'immobilier, le voyage et certaines professions réglementées. Nous vérifions votre obligation avec vous." }],
    },
    en: {
      name: "Financial guarantee",
      benefit: "Meet the regulatory requirements of your profession.",
      heroTitle: "The financial guarantee your activity requires.",
      heroText: "Some professions must provide a financial guarantee. We look for a solution suited to your status and obligations.",
      profiles: ["Estate agents", "Travel agencies", "Agents and regulated professions", "Businesses under obligation"],
      risks: ["Representation of funds held for third parties", "Regulatory compliance", "Securing clients and partners"],
      complex: ["Newly created activity", "High guarantee amounts", "Financial situation to present"],
      prepare: ["The exact nature of your obligation", "The required guarantee amount", "Your activity details"],
      faq: [{ q: "Which professions are concerned?", a: "Notably real estate, travel and certain regulated professions. We check your obligation with you." }],
    },
  },
  // ---------------------- ÉPARGNE ----------------------
  {
    slug: "assurance-vie",
    category: "epargne",
    icon: "PiggyBank",
    image: "https://images.pexels.com/photos/3943716/pexels-photo-3943716.jpeg",
    fr: {
      name: "Assurance-vie",
      benefit: "Faire fructifier votre épargne et préparer la transmission.",
      heroTitle: "Épargner, valoriser et transmettre avec l'assurance-vie.",
      heroText: "Nous vous accompagnons dans la construction d'un contrat cohérent avec vos objectifs, votre horizon et votre tolérance au risque.",
      profiles: ["Épargnants souhaitant valoriser un capital", "Personnes préparant la transmission", "Investisseurs de long terme", "Frontaliers résidents fiscaux français"],
      risks: ["Épargne disponible et pilotable", "Support en euros et unités de compte", "Préparation de la transmission", "Souplesse des versements et retraits"],
      complex: ["Objectifs mixtes épargne / transmission", "Arbitrages entre sécurité et performance", "Situation patrimoniale spécifique"],
      prepare: ["Vos objectifs d'épargne", "Votre horizon de placement", "Votre tolérance au risque"],
      faq: [{ q: "L'assurance-vie comporte-t-elle un risque ?", a: "Les supports en unités de compte présentent un risque de perte en capital. Le fonds en euros offre davantage de sécurité. Nous en discutons selon votre profil." }],
      riskNote: "Les unités de compte présentent un risque de perte en capital. Aucun rendement n'est garanti. Les avantages fiscaux dépendent de votre situation et de la réglementation.",
    },
    en: {
      name: "Life insurance",
      benefit: "Grow your savings and prepare transmission.",
      heroTitle: "Save, grow and pass on with life insurance.",
      heroText: "We support you in building a contract consistent with your objectives, horizon and risk tolerance.",
      profiles: ["Savers wishing to grow capital", "People preparing transmission", "Long-term investors", "French-tax-resident cross-border workers"],
      risks: ["Available and manageable savings", "Euro fund and unit-linked supports", "Transmission preparation", "Flexible payments and withdrawals"],
      complex: ["Mixed savings / transmission goals", "Trade-offs between security and performance", "Specific wealth situation"],
      prepare: ["Your savings objectives", "Your investment horizon", "Your risk tolerance"],
      faq: [{ q: "Does life insurance carry a risk?", a: "Unit-linked supports carry a risk of capital loss. The euro fund offers more security. We discuss this based on your profile." }],
      riskNote: "Unit-linked supports carry a risk of capital loss. No return is guaranteed. Tax benefits depend on your situation and regulations.",
    },
  },
  {
    slug: "per",
    category: "epargne",
    icon: "TrendingUp",
    image: "https://images.pexels.com/photos/7821487/pexels-photo-7821487.jpeg",
    fr: {
      name: "Plan d'Épargne Retraite (PER)",
      benefit: "Préparer votre retraite tout en optimisant votre effort d'épargne.",
      heroTitle: "Construire votre retraite, à votre rythme.",
      heroText: "Le PER permet de se constituer un complément de revenus pour la retraite. Nous vous aidons à choisir une solution cohérente avec votre situation.",
      profiles: ["Actifs préparant leur retraite", "Indépendants et TNS", "Dirigeants", "Frontaliers résidents fiscaux français"],
      risks: ["Constitution d'une épargne retraite", "Sortie en capital ou en rente", "Gestion pilotée ou libre", "Transférabilité des anciens contrats"],
      complex: ["Optimisation selon votre situation fiscale", "Arbitrage entre disponibilité et objectif retraite", "Transfert de contrats existants"],
      prepare: ["Votre horizon de départ à la retraite", "Votre capacité d'épargne", "Vos éventuels contrats retraite existants"],
      faq: [{ q: "L'épargne d'un PER est-elle bloquée ?", a: "Elle est en principe destinée à la retraite, avec des cas de déblocage anticipé prévus par la loi (achat de résidence principale, accidents de la vie…)." }],
      riskNote: "Selon les supports choisis, un risque de perte en capital peut exister. Les avantages fiscaux dépendent de votre situation et de la réglementation.",
    },
    en: {
      name: "Retirement Savings Plan (PER)",
      benefit: "Prepare your retirement while optimising your savings effort.",
      heroTitle: "Build your retirement, at your own pace.",
      heroText: "The PER lets you build additional retirement income. We help you choose a solution consistent with your situation.",
      profiles: ["Working people preparing retirement", "Self-employed", "Company directors", "French-tax-resident cross-border workers"],
      risks: ["Building retirement savings", "Capital or annuity exit", "Managed or free management", "Transferability of old contracts"],
      complex: ["Optimisation based on your tax situation", "Trade-off between availability and retirement goal", "Transfer of existing contracts"],
      prepare: ["Your retirement horizon", "Your savings capacity", "Any existing retirement contracts"],
      faq: [{ q: "Is PER savings locked?", a: "It is in principle intended for retirement, with early-release cases provided by law (main-home purchase, life accidents, etc.)." }],
      riskNote: "Depending on the supports chosen, a risk of capital loss may exist. Tax benefits depend on your situation and regulations.",
    },
  },
];

export const getServiceRoute = (s) => `${basePaths[s.category]}/${s.slug}`;
export const getServiceBySlug = (slug) => services.find((s) => s.slug === slug);
export const getServicesByCategory = (cat) => services.filter((s) => s.category === cat);
